/**
 * iter 148: Historical quotations dialog for a single customer. Lists
 * every cart in `/api/carts` whose customer_id matches, sorted newest-first.
 * "Open" navigates to /cart and passes the cart id via location state so
 * CartPage can auto-select it.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

export default function CustomerQuotesDialog({ open, onOpenChange, customer }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !customer) return;
    setLoading(true);
    api
      .get("/carts")
      .then(({ data }) => setQuotes((data || []).filter((c) => c.customer_id === customer.id)))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load quotes"))
      .finally(() => setLoading(false));
  }, [open, customer]);

  const openQuote = (cartId) => {
    onOpenChange(false);
    navigate("/cart", { state: { autoSelectCartId: cartId } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-3xl" data-testid="customer-quotes-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-gray-900">
            Quote History
            <span className="ml-2 text-xs font-normal text-gray-500">{customer?.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400" data-testid="customer-quotes-loading">Loading…</div>
          ) : quotes.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400" data-testid="customer-quotes-empty">
              No quotations for this customer yet.
            </div>
          ) : (
            <div className="border border-gray-100 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 hover:bg-gray-50 border-b border-gray-100">
                    <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Name</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Currency</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Items</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Coeff</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">Created</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">Open</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((c) => (
                    <TableRow key={c.id} className="border-b border-gray-50" data-testid={`customer-quote-row-${c.id}`}>
                      <TableCell className="text-sm font-semibold text-gray-900">{c.name}</TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">{c.currency || "-"}</TableCell>
                      <TableCell className="font-mono text-sm text-right text-gray-800">{c.item_count ?? "-"}</TableCell>
                      <TableCell className="font-mono text-sm text-right text-gray-800">{c.price_coefficient != null ? Number(c.price_coefficient).toFixed(2) : "-"}</TableCell>
                      <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString("en-US") : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          data-testid={`customer-quote-open-${c.id}`}
                          onClick={() => openQuote(c.id)}
                          className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700"
                        >
                          Open <ExternalLink size={12} strokeWidth={1.75} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
